import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

import {
  supabaseClient, getDbHeaders, SUPABASE_URL,
  fromRow, toRow, dbLoadEntries, dbInsertEntry, dbUpdateEntry, dbDeleteEntry,
  dbPurgeOldEntries, dbPurgeOldAnalytics, dbBatchUpdateEntries,
  dbLoadSCCO, dbLoadSCCOFull, dbAddSCCO, dbDeleteSCCO, dbLoadContacts, dbUpsertContactPhone,
  dbLoadMessages, dbSendMessage, dbMarkMessagesRead, dbDeleteMessage,
  dbDeleteAllMessages, dbPurgeOldMessages,
  dbSetTyping, dbClearTyping, dbGetTyping,
  dbSavePushSubscription, dbDeletePushSubscription,
  dbSubmitFeedback, dbLoadFeedback, dbMarkFeedbackResolved, dbDeleteFeedback,
  dbUpdateSccoPassword, dbUpdateAirlinePassword, dbSetSccoAdmin,
  dbGetLoginAlerts,
} from "./db";
import { DARK_THEME, LIGHT_THEME, DARK_STATUS_STYLES, LIGHT_STATUS_STYLES } from "./theme";
import { SESSION_KEY, loadSession, saveSession, clearSession } from "./utils/sessionUtils";
import { getLocal, setLocal, getSession, setSession } from "./utils/storageUtils";
import { toMins, parseShift, calcSection, applyShiftToEntries, getAirlineCode, formatDateLabel } from "./utils/timeUtils";
import { exportCSV } from "./utils/exportUtils";
import { lookupRouteCache, learnRoutes } from "./utils/routeCache";
import { useLockBodyScroll } from "./hooks/useLockBodyScroll";
import { useWindowWidth } from "./hooks/useWindowWidth";
import { useEscapeKey } from "./hooks/useEscapeKey";
import { useSwipeToClose } from "./hooks/useSwipeToClose";
import { SA_AIRLINES, LHG_CARRIERS, AirlineLogo, StarMark, FieldLabel, inputStyle, TimeInput, TextInput, NumberInput, SectionDivider, AirlineSelector, Button } from "./components/shared";
import { ExportShiftModal } from "./components/modals/ExportShiftModal";
import { LoginScreen } from "./components/modals/LoginScreen";
import { AddEntryModal } from "./components/modals/AddEntryModal";
import { MessagesModal } from "./components/modals/MessagesModal";
import { playEntryChime, playMessageChime } from "./utils/audioUtils";
import { useTheme } from "./hooks/useTheme";
import { ThemeContext, useThemeContext } from "./theme/ThemeContext";
import { overlayBackdrop } from "./theme/styles";
import { DARK_TOKENS, TYPOGRAPHY, RADIUS } from "./theme/tokens";
import { useAuth, canNotify, loadPushPref, savePushPref, registerServiceWorker, requestNotificationPermission, subscribeToPush, unsubscribeFromPush } from "./hooks/useAuth";
import { useMessages } from "./hooks/useMessages";
import { useEntries } from "./hooks/useEntries";
import { useModals } from "./hooks/useModals";
import { MODAL_CLOSE_DELAY_MS, TOAST_DURATION_MS } from "./utils/constants";
import { CHANGELOG } from "./utils/changelog";
import { appendRemark } from "./utils/entryUtils";
import { loadHiddenIds, saveHiddenIds } from "./utils/hiddenEntries";
import { ChangeDateModal } from "./components/modals/ChangeDateModal";
import { ShiftSetupModal, SCCOPicker } from "./components/modals/ShiftSetupModal";
import { ExcelImportModal } from "./components/modals/ExcelImportModal";
import { FeedbackModal } from "./components/modals/FeedbackModal";
import { FeedbackInboxModal } from "./components/modals/FeedbackInboxModal";
import { LoginAlertsModal } from "./components/modals/LoginAlertsModal";
import { LoginLogModal } from "./components/modals/LoginLogModal";
import { ChangelogModal } from "./components/modals/ChangelogModal";
import { PartnerRemarkModal } from "./components/modals/PartnerRemarkModal";
import { AmenityRequestModal } from "./components/modals/AmenityRequestModal";
import { PartnerBoardedModal } from "./components/modals/PartnerBoardedModal";
import { ChangePasswordModal } from "./components/modals/ChangePasswordModal";
import { AdminPasswordModal } from "./components/modals/AdminPasswordModal";
import { ViewEntryModal } from "./components/modals/ViewEntryModal";
import { WillHoldModal } from "./components/modals/WillHoldModal";
import { ActivityHeatmapModal } from "./components/modals/ActivityHeatmapModal";
import { OpsIntelModal } from "./components/modals/OpsIntelModal";
import { WarRoomModal } from "./components/modals/WarRoomModal";
import { ExpeditorModal } from "./components/modals/ExpeditorModal";
import { EntryCard } from "./components/EntryCard";
import StatsBar from "./components/StatsBar";
import AppHeader from "./components/AppHeader";
import FilterBar from "./components/FilterBar";
import HamburgerMenu, { todayISO } from "./components/HamburgerMenu";
import { PhoneDirectoryModal } from "./components/PhoneDirectoryModal";
import { SCCORosterModal } from "./components/SCCORosterModal";
import { InactivityModal } from "./components/InactivityModal";
import { IOSInstallBanner } from "./components/IOSInstallBanner";


import { SA_LOGO } from "./utils/saLogo";


// -- Supabase config (REST API -- no SDK needed) -------------------------------

// ---------------------------------------------------------------------------
// MCT (Minimum Connecting Time) thresholds for LAX Star Alliance connections
// ---------------------------------------------------------------------------
// SOURCE: Official Star Alliance LAX Connection Matrix (Feb 2026)
//         CONNECTION_MATRIX_LAX_INBOUNDAIRLINE_ALL_OUTBOUNDAIRLINE_ALL.xlsx
//
// The matrix defines Upper and Lower limits per inbound/outbound airline pair
// and connection type. These map to the three status zones as follows:
//   < Lower Limit  →  Unachievable
//   >= Lower Limit and < Upper Limit  →  At Risk
//   >= Upper Limit  →  Achievable
//
// Connection types at LAX:
//   ID = Interline Domestic (TBIT partner inbound → UA/AC outbound)
//   DI = Domestic Interline (UA/AC inbound → TBIT partner outbound)
//   TD = Terminal Domestic  (AC inbound → UA outbound, same terminal complex)
//   DT = Domestic Terminal  (UA inbound → AC outbound, same terminal complex)
//
// Thresholds:
//
//  TBIT Partners inbound (LH, NZ, NH, SQ, LX, OZ, CA, CM, AV, etc.) — Type ID
//    - Arrive Tom Bradley International Terminal, clear US customs, shuttle/walk to T7/8
//    - Unachievable : < 60 min
//    - At Risk      : 60 – 119 min
//    - Achievable   : >= 120 min
//
//  AC inbound → partner outbound — Type DI
//    - Arrives Terminal 6 (pre-cleared customs in Canada), connecting to TBIT partner
//    - Unachievable : < 60 min
//    - At Risk      : 60 – 89 min
//    - Achievable   : >= 90 min
//
//  AC inbound → UA outbound — Type TD
//    - Arrives Terminal 6, short walk to UA Terminals 7/8, no customs
//    - Unachievable : < 30 min
//    - At Risk      : 30 – 59 min
//    - Achievable   : >= 60 min
//
//  UA inbound → partner outbound — Type DI
//    - UA domestic arrival, connecting to TBIT partner
//    - Unachievable : < 60 min
//    - At Risk      : 60 – 89 min
//    - Achievable   : >= 90 min
//
//  UA inbound → AC outbound — Type DT
//    - UA domestic arrival, connecting to AC at Terminal 6
//    - Unachievable : < 30 min
//    - At Risk      : 30 – 59 min
//    - Achievable   : >= 60 min
//
// TODO: Wire this up when a direct API or live data feed replaces the
//       Excel import workflow.
// ---------------------------------------------------------------------------
// MCT_THRESHOLDS moved to src/utils/mctUtils.js
// ---------------------------------------------------------------------------



const STATUS_OPTIONS = ["SUCCESS", "PARTIAL SUCCESS", "UNSUCCESSFUL", "REBOOKED", "PROTECTED", "UNABLE TO CONFIRM"];

// -- Utilities ----------------------------------------------------------------

function sendPushNotification(title, body, tag, data = {}) {
  if (!canNotify()) return;
  // When the app is visible the in-app chime/banner already handles alerting the user.
  // Showing a system notification on top would cause a duplicate (server push already
  // fired one via the SW). Only show when the app is hidden/backgrounded.
  if (document.visibilityState === "visible") return;
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "SHOW_NOTIFICATION", title, body, tag, data });
    } else {
      new Notification(title, { body, icon: "/logo192.png", tag, data });
    }
  } catch (e) { console.warn("Notification error:", e); }
}

// -- Storage helpers ----------------------------------------------------------
// Date stays local (each user tracks their own view date)
const STORAGE_DATE_KEY = "sa_lax_current_date";
function loadCurrentDate() {
  return getLocal(STORAGE_DATE_KEY, null) || todayISO();
}
function saveCurrentDate(date) {
  setLocal(STORAGE_DATE_KEY, date);
}

// -- Supabase entry helpers ----------------------------------------------------
// -- Shift storage ------------------------------------------------------------
const STORAGE_SHIFT_KEY = "sa_lax_shift";

function loadShift() {
  return getLocal(STORAGE_SHIFT_KEY, null);
}

function saveShift(shift) {
  setLocal(STORAGE_SHIFT_KEY, shift);
}



// Detect when this window was opened as a Mission Control popup
const IS_MC_POPUP = new URLSearchParams(window.location.search).get("mc") === "1";

// Rendered when the popup's session ends — calls window.close() in an effect
// so the browser actually honours it (close() during render is ignored).
function MCPopupClosing() {
  useEffect(() => { window.close(); }, []);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#060912", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ color: "#4A5270", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 13, letterSpacing: "0.1em" }}>
        MISSION CONTROL
      </span>
    </div>
  );
}

export default function App() {
  const explicitSignOutRef = useRef(false);

  // App-level UI state
  const [appDate,           setAppDate]           = useState(() => loadCurrentDate());
  const [shift,             setShift]             = useState(() => loadShift());
  const {
    showEntry, setShowEntry, showChangeDate, setShowChangeDate,
    showMenu, setShowMenu, showShiftSetup, setShowShiftSetup,
    showImportExcel, setShowImportExcel, showSCCOPicker, setShowSCCOPicker,
    showAdminPw, setShowAdminPw, showChangePw, setShowChangePw,
    showExportShift, setShowExportShift, showSCCORoster, setShowSCCORoster,
    showPhoneDir, setShowPhoneDir, showFeedback, setShowFeedback,
    showFeedbackInbox, setShowFeedbackInbox, showLoginAlerts, setShowLoginAlerts,
    showLoginLog, setShowLoginLog,
    showActivityHeatmap, setShowActivityHeatmap,
    showOpsIntel, setShowOpsIntel,
    showWarRoom, setShowWarRoom,
    showExpeditor, setShowExpeditor,
  } = useModals();
  const [initialImportFile, setInitialImportFile] = useState(null);
  const [globalDragging,    setGlobalDragging]    = useState(false);
  const dragCounter = useRef(0);
  const mcPopupRef  = useRef(null);

  const [bgBlobs] = useState(() => {
    const COLORS = [
      "74,124,247",   // blue
      "120,80,220",   // indigo
      "220,120,60",   // amber
      "40,180,200",   // teal
      "200,70,130",   // rose
      "80,160,120",   // green
    ];
    const count = Math.floor(Math.random() * 3) + 4; // 4–6 blobs
    return Array.from({ length: count }, () => ({
      left:   Math.random() * 100,
      top:    Math.random() * 100,
      width:  Math.random() * 380 + 220,
      height: Math.random() * 380 + 220,
      color:  COLORS[Math.floor(Math.random() * COLORS.length)],
      opacity: Math.random() * 0.18 + 0.12,
    }));
  });

  const [unreadFeedback,    setUnreadFeedback]    = useState(0);

  const [lockoutAlertCount, setLockoutAlertCount] = useState(0);

  const [syncEnabled,       setSyncEnabled]       = useState(true);
  const [partnerRemark,     setPartnerRemark]     = useState(null);
  const [uaRemark,          setUaRemark]          = useState(null);
  const [hiddenIds,         setHiddenIds]         = useState(new Set());
  const [partnerBoarded,    setPartnerBoarded]    = useState(null);
  const [amenityEntry,      setAmenityEntry]      = useState(null);
  const [willHoldEntry,     setWillHoldEntry]     = useState(null);
  const [messageDraft,      setMessageDraft]      = useState(null);

  const { isDark, toggleTheme, SA, STATUS_STYLES, GLASS, tokens, SPACING, TYPOGRAPHY, RADIUS, headerRef, headerHeight, fontScale, setFontScale } = useTheme();
  const {
    session, setSession,
    activeSCCO, setActiveSCCO,
    sccoList, setSccoList,
    isUA, myAirline, partnerName, isAdmin,
    doSignOut,
    showInactivity, setShowInactivity,
    inactivitySecsLeft,
    resetInactivity,
    pushEnabled, setPushEnabled,
    pushPermission, setPushPermission,
  } = useAuth();

  const parsedShift = parseShift(shift);

  const {
    messages,
    showMessages, setShowMessages,
    openThread, setOpenThread,
    urgentBanner, setUrgentBanner,
    seenUrgent, setSeenUrgent,
    confirmDeleteAll, setConfirmDeleteAll,
    unread,
    loadMessages,
    handleDismissUrgentBanner,
    handleSendMessage,
    handleBroadcast,
    handleDeleteMessage,
    handleDeleteAllMessages,
    handleMarkRead,
    broadcastReceipts,
    handleRecordBroadcastReceipts,
    handleLoadBroadcastReceipts,
    amenityStatusMap,
    handleAmenityResponse,
  } = useMessages({ session, myAirline, isUA, appDate, activeSCCO, partnerName });

  const {
    entries, setEntries, loading, newEntryIds, clearNewEntryId,
    importedEntryIds, setImportedEntryIds,
    editEntry, setEditEntry, viewEntry, setViewEntry,
    activeTab, setActiveTab,
    filterCompletion, setFilterCompletion,
    filterStatus, setFilterStatus,
    filterInAirline, setFilterInAirline,
    filterOutAirline, setFilterOutAirline,
    filterOrigin, setFilterOrigin,
    filterDest, setFilterDest,
    filterOverdue, setFilterOverdue,
    filterHideP2P, setFilterHideP2P,
    filterTimeFrom, setFilterTimeFrom,
    filterTimeTo, setFilterTimeTo,
    showFilterModal, setShowFilterModal,
    sortBy, setSortBy, sortDir, setSortDir,
    visibleEntries, filterOptions, filtered, sorted,
    isComplete, monitoringCount,
    handleDelete, handleSave, handleReopen, handleEdit, handleMarkSuccessful,
    handleImport, handlePin, toggleSort,
  } = useEntries({ appDate, parsedShift, isUA, myAirline, activeSCCO, sessionReady: !!session });

  const { handleProps: filterHandleProps, dragStyle: filterDragStyle } = useSwipeToClose(
    useCallback(() => setShowFilterModal(false), [setShowFilterModal])
  );

  // Wraps handleAmenityResponse to also append a remark to the entry card
  // Must live after useEntries so entries/setEntries are initialized before this useCallback runs
  const handleAmenityResponseWithRemark = useCallback(async (msg, status, delayType, requestContactInfo = false, contactNames = [], denialReason = "") => {
    await handleAmenityResponse(msg, status, delayType, requestContactInfo, contactNames, denialReason);
    const entry = entries.find(e => e.id === msg.connection_id);
    if (!entry) return;
    const reasonSuffix = status === "denied" && denialReason ? ` — ${denialReason}` : "";
    const label = status === "approved"
      ? `Amenities approved — ${delayType} delay`
      : `Amenities denied — ${delayType} delay${reasonSuffix}`;
    const updated = { ...entry, remark: appendRemark(entry.remark, label, "UA", activeSCCO?.name || "SCCO") };
    await dbUpdateEntry(updated, appDate);
    setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
  }, [handleAmenityResponse, entries, appDate, activeSCCO, setEntries]);

const winWidth = useWindowWidth();
  const isWide   = winWidth >= 768;

  const handleHideEntry = useCallback((id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(String(id));
      saveHiddenIds(activeSCCO.id, appDate, next);
      return next;
    });
  }, [activeSCCO?.id, appDate]);

  const handleUnhideEntry = useCallback((id) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.delete(String(id));
      saveHiddenIds(activeSCCO.id, appDate, next);
      return next;
    });
  }, [activeSCCO?.id, appDate]);

  const handleUnhideAll = useCallback(() => {
    setHiddenIds(new Set());
    saveHiddenIds(activeSCCO.id, appDate, new Set());
  }, [activeSCCO?.id, appDate]);
  const [sortMenuRect, setSortMenuRect] = useState(null);

  const warRoomEntries = useMemo(
    () => visibleEntries.filter(e => !e.status || e.status === "" || e.status === "PROTECTED"),
    [visibleEntries]
  );

  const displayedEntries = useMemo(
    () => isUA ? sorted.filter(e => !hiddenIds.has(String(e.id))) : sorted,
    [isUA, sorted, hiddenIds]
  );

  // Close FilterModal on Escape
  useEffect(() => {
    if (!showFilterModal) return;
    const handler = e => { if (e.key === "Escape") setShowFilterModal(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showFilterModal]);

  // Mission Control popup: restore session from localStorage bridge, then auto-open war room
  useEffect(() => {
    if (!IS_MC_POPUP) return;
    const bridge = localStorage.getItem("sa_mc_bridge");
    if (!bridge) return;
    try {
      const { access_token, refresh_token, app_session, active_scco, ts } = JSON.parse(bridge);
      localStorage.removeItem("sa_mc_bridge");
      if (Date.now() - ts < 30_000) {
        supabaseClient.auth.setSession({ access_token, refresh_token });
        if (app_session) { saveSession(app_session); setSession(app_session); }
        if (active_scco) setActiveSCCO(active_scco);
      }
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (IS_MC_POPUP && session) setShowWarRoom(true);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the Mission Control popup when the main window signs out.
  // Direct close via the stored window reference is the only reliable method.
  useEffect(() => {
    if (!session && !IS_MC_POPUP) {
      mcPopupRef.current?.close();
      mcPopupRef.current = null;
    }
  }, [session]);

  // Save date preference locally
  useEffect(() => { if (session) saveCurrentDate(appDate); }, [appDate, session]);

  // Load per-SCCO hidden entry IDs from localStorage; auto-resets when date changes
  useEffect(() => {
    if (activeSCCO?.id && appDate) {
      setHiddenIds(loadHiddenIds(activeSCCO.id, appDate));
    }
  }, [activeSCCO?.id, appDate]);

  // Purge entries older than 7 days on startup; analytics kept 90 days (runs once)
  useEffect(() => { if (session) { dbPurgeOldEntries(); dbPurgeOldAnalytics(); } }, [session]);

  // Load unread feedback count for admins
  useEffect(() => {
    if (!isAdmin || !session) return;
    const loadFeedbackCount = async () => {
      try {
        const res = await fetch(SUPABASE_URL + "/rest/v1/feedback?resolved=eq.false&select=id", { headers: await getDbHeaders() });
        const data = await res.json();
        setUnreadFeedback(Array.isArray(data) ? data.length : 0);
      } catch (_) {}
    };
    loadFeedbackCount();
  }, [isAdmin, session, showFeedbackInbox]);

  // Load lockout alert count for admins
  useEffect(() => {
    if (!isAdmin || !session) return;
    dbGetLoginAlerts().then(rows => setLockoutAlertCount(rows.length));
  }, [isAdmin, session, showLoginAlerts]);

  // Load remote sync-enabled flag for admins
  useEffect(() => {
    if (!isAdmin || !session) return;
    const load = async () => {
      try {
        const res = await fetch(SUPABASE_URL + "/rest/v1/sync_settings?key=eq.auto_sync_enabled&select=value", { headers: await getDbHeaders() });
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) setSyncEnabled(!!data[0].value);
      } catch (_) {}
    };
    load();
  }, [isAdmin, session]);

  const handleToggleSync = async () => {
    const next = !syncEnabled;
    setSyncEnabled(next);
    try {
      await fetch(SUPABASE_URL + "/rest/v1/sync_settings?key=eq.auto_sync_enabled", {
        method: "PATCH",
        headers: await getDbHeaders({ Prefer: "return=minimal" }),
        body: JSON.stringify({ value: next, updated_by: activeSCCO?.name || "admin", updated_at: new Date().toISOString() }),
      });
    } catch (_) { setSyncEnabled(!next); } // revert on error
  };

  // Register service worker for push notifications
  useEffect(() => { registerServiceWorker(); }, []);

  // PWA app icon badge — unread messages + unopened new connection cards.
  useEffect(() => {
    if (!session) return;
    const total = unread + newEntryIds.size;
    if ("setAppBadge" in navigator) {
      if (total > 0) {
        navigator.setAppBadge(total).catch(() => {});
      } else {
        navigator.clearAppBadge().catch(() => {});
      }
    }
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "SYNC_BADGE", count: total });
    }
  }, [unread, newEntryIds, session]);

  // Scroll to top after first entry load following each login.
  // Flag is set synchronously in render (not in an effect) so it's guaranteed to be
  // visible to the useLayoutEffect in the same or any later commit.
  // rAF fires after browser layout/scroll-anchoring so our reset wins.
  const scrollAfterLoad = useRef(false);
  const prevSession = useRef(null);
  if (!prevSession.current && session) scrollAfterLoad.current = true;
  prevSession.current = session;
  useLayoutEffect(() => {
    if (!loading && scrollAfterLoad.current) {
      scrollAfterLoad.current = false;
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
      });
    }
  }, [loading]);

  // Global drag-and-drop for Excel import — UA SCCOs only
  useEffect(() => {
    if (!isUA || !session) return;
    const onDragEnter = (e) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      dragCounter.current++;
      if (dragCounter.current === 1) setGlobalDragging(true);
    };
    const onDragLeave = () => {
      dragCounter.current--;
      if (dragCounter.current <= 0) { dragCounter.current = 0; setGlobalDragging(false); }
    };
    const onDragOver = (e) => { if (e.dataTransfer.types.includes("Files")) e.preventDefault(); };
    const onDrop = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setGlobalDragging(false);
      if (showImportExcel) return; // modal already open — let it handle the drop
      const file = e.dataTransfer.files[0];
      if (file && /\.(xlsx|xls|csv)$/i.test(file.name)) {
        setInitialImportFile(file);
        setShowImportExcel(true);
      }
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover",  onDragOver);
    window.addEventListener("drop",      onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover",  onDragOver);
      window.removeEventListener("drop",      onDrop);
      dragCounter.current = 0;
      setGlobalDragging(false);
    };
  }, [isUA, session, showImportExcel]);

  const completeCount = useMemo(
    () => visibleEntries.filter(e => isComplete(e)).length,
    [visibleEntries]
  );

  const { stats, successRate } = useMemo(() => {
    const s = visibleEntries.reduce((a, e) => {
      const ttl = Number(e.ttlPax) || 0;
      const exp = Number(e.paxExp) || 0;
      const rbk = Number(e.paxRbk) || 0;
      const complete = isComplete(e);
      return {
        pax:     a.pax  + (complete ? 0 : ttl),
        exp:     a.exp  + exp,
        bags:    a.bags + (Number(e.bagsExp) || 0),
        // PAX-based success: expedited + rebooked PAX from completed connections
        succPax: a.succPax + (complete ? exp + rbk : 0),
        totalPax: a.totalPax + (complete ? ttl : 0),
      };
    }, { pax: 0, exp: 0, bags: 0, succPax: 0, totalPax: 0 });
    return { stats: s, successRate: s.totalPax > 0 ? Math.round(s.succPax / s.totalPax * 100) : 0 };
  }, [visibleEntries]);

  // Show login screen if not authenticated -- placed here after all hooks
  if (!session) {
    if (IS_MC_POPUP) return <MCPopupClosing />;

    return <LoginScreen
      SA={DARK_THEME}
      GLASS={GLASS}
      version={CHANGELOG[0].version}
      saLogoData={SA_LOGO}
      autoFaceId={!explicitSignOutRef.current}
      onLogin={s => {
      explicitSignOutRef.current = false;
      setSession(s);
      setAppDate(todayISO());
      if (s.scco) setActiveSCCO(s.scco);
    }} />;
  }


  const handleShiftSave = (newShift, isFirst) => {
    saveShift(newShift);
    setShift(newShift);
    const parsed = parseShift(newShift);
    setEntries(prev => applyShiftToEntries(prev, parsed));
    setShowShiftSetup(false);
    if (isFirst) setShowImportExcel(true);
  };

  const handleSCCOAddNew = async (name, badge) => {
    const created = await dbAddSCCO(name, badge);
    if (created) setSccoList(prev => [...prev, created].sort((a,b) => a.name.localeCompare(b.name)));
    return created;
  };

  const handleChangeDate = (newDate) => {
    setAppDate(newDate);
    setFilterStatus("ALL");
    setFilterInAirline("ALL");
    setFilterOutAirline("ALL");
    setFilterOrigin("ALL");
    setFilterDest("ALL");
    setFilterOverdue(false);
    setActiveTab("active");
    setFilterCompletion("monitoring");
    setShowChangeDate(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilterCompletion("monitoring");
    setFilterStatus("ALL");
    setConfirmDeleteAll(false);
  };

  return (
    <ThemeContext.Provider value={{ SA, STATUS_STYLES, GLASS, tokens, isDark, toggleTheme, SPACING, TYPOGRAPHY, RADIUS }}>
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@100;200;300;400;500;600;700&display=swap');
        @font-face {
          font-family: 'HelveticaNeueStd';
          src: url('/fonts/HelveticaNeueLTStd-ThEx.otf') format('opentype');
          font-weight: 300;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: 'HelveticaNeueStd';
          src: url('/fonts/HelveticaNeueLTStd-Ex.otf') format('opentype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { width: 100%; background: ${(isDark || !session)
          ? "linear-gradient(135deg,#060912 0%,#080E1C 45%,#0A1020 70%,#060912 100%)"
          : "linear-gradient(135deg,#B8CCF5 0%,#C4D4FA 50%,#B2C4EE 100%)"}; }
        html { overflow-x: hidden; overscroll-behavior: none; max-width: 100vw; overflow-anchor: none; }
        body { overscroll-behavior: none; max-width: 100vw; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${(isDark || !session) ? SA.borderGray : SA.lightSilver}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${(isDark || !session) ? "#3E4450" : "#A8B0BC"}; }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input::placeholder { color: ${(isDark || !session) ? SA.borderGray : "#A8B0BC"}; }
        textarea::placeholder { color: ${(isDark || !session) ? SA.borderGray : "#A8B0BC"}; }
        select option { background: ${(isDark || !session) ? SA.darkGray : "#FFFFFF"}; color: ${(isDark || !session) ? SA.white : "#0F1117"}; }
        input[type=time]::-webkit-calendar-picker-indicator { filter: invert(0.3); }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.4); }
        button { font-family: 'Barlow', 'Helvetica Neue', Arial, sans-serif; }
        @keyframes glassShimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes modalSlideUp { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes sheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes msgPulseUrgent {
          0%,100% { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 6px 1px rgba(210,80,80,0.0); }
          40%     { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 22px 8px rgba(210,80,80,0.55), 0 0 8px 3px rgba(210,80,80,0.28); }
          60%     { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 14px 4px rgba(210,80,80,0.30), 0 0 5px 1px rgba(210,80,80,0.14); }
        }
        @keyframes msgPulseUnread {
          0%,100% { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 6px 1px rgba(200,168,48,0.0); }
          40%     { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 22px 8px rgba(200,168,48,0.52), 0 0 8px 3px rgba(200,168,48,0.26); }
          60%     { box-shadow: inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.18), 0 0 14px 4px rgba(200,168,48,0.28), 0 0 5px 1px rgba(200,168,48,0.12); }
        }
        .msg-btn-urgent { animation: msgPulseUrgent 1.8s ease-in-out infinite; }
        .msg-btn-unread { animation: msgPulseUnread 2.4s ease-in-out infinite; }
        .glass-fab:hover { transform: scale(1.08); box-shadow: ${isDark ? "inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 18px 40px rgba(0,0,0,0.38), 0 4px 12px rgba(0,0,0,0.18)" : "inset 0 1.5px 0 rgba(255,255,255,0.90), 0 4px 12px rgba(0,0,0,0.14), 0 12px 36px rgba(0,0,0,0.18)"} !important; filter: brightness(1.05); }
        .glass-fab:active { transform: scale(0.94); }
        html { background: ${(isDark || !session) ? "#060912" : "#B8CCF5"}; }
        @keyframes newCardGlow {
          0%   { box-shadow: 0 2px 12px ${isDark ? "rgba(74,124,247,0.0)" : "rgba(37,99,235,0.0)"}; }
          15%  { box-shadow: 0 0 24px 8px ${isDark ? "rgba(74,124,247,0.5)" : "rgba(37,99,235,0.75)"}; }
          30%  { box-shadow: 0 0 8px  3px ${isDark ? "rgba(74,124,247,0.15)" : "rgba(37,99,235,0.3)"}; }
          50%  { box-shadow: 0 0 20px 6px ${isDark ? "rgba(74,124,247,0.4)" : "rgba(37,99,235,0.6)"}; }
          70%  { box-shadow: 0 0 8px  3px ${isDark ? "rgba(74,124,247,0.15)" : "rgba(37,99,235,0.3)"}; }
          100% { box-shadow: 0 2px 12px ${isDark ? "rgba(74,124,247,0.0)" : "rgba(37,99,235,0.0)"}; }
        }
        .entry-card-new { animation: newCardGlow 5s ease-out forwards; }
        @keyframes cardEntrance {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes logoSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .logo-spinning {
          animation: logoSpin 0.7s linear infinite !important;
        }
@keyframes filterSheetUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes drawerSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes urgencyPulse {
          0%   { box-shadow: 0 2px 12px rgba(0,0,0,0.3), 0 0 0px 0px rgba(251,146,60,0); border-color: rgba(251,146,60,0.3); border-top-color: rgba(251,146,60,0.5); }
          50%  { box-shadow: 0 2px 24px rgba(0,0,0,0.4), 0 0 24px 6px rgba(251,146,60,0.25); border-color: rgba(251,146,60,0.7); border-top-color: rgba(251,146,60,1); }
          100% { box-shadow: 0 2px 12px rgba(0,0,0,0.3), 0 0 0px 0px rgba(251,146,60,0); border-color: rgba(251,146,60,0.3); border-top-color: rgba(251,146,60,0.5); }
        }
        @keyframes importedCardGlow {
          0%   { box-shadow: 0 2px 12px rgba(0,0,0,0.3), 0 0 0px 0px rgba(74,200,120,0); border-color: rgba(74,200,120,0.3); border-top-color: rgba(74,200,120,0.5); }
          12%  { box-shadow: 0 0 28px 10px rgba(74,200,120,0.45); border-color: rgba(74,200,120,0.8); border-top-color: rgba(74,200,120,1); }
          40%  { box-shadow: 0 0 14px 4px rgba(74,200,120,0.2); border-color: rgba(74,200,120,0.5); border-top-color: rgba(74,200,120,0.7); }
          100% { box-shadow: 0 2px 12px rgba(0,0,0,0.3), 0 0 0px 0px rgba(74,200,120,0); border-color: rgba(74,200,120,0.15); border-top-color: rgba(74,200,120,0.2); }
        }
        .entry-card-inner { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
        .entry-card-imported .entry-card-inner { animation: importedCardGlow 7s ease-out forwards; }
        .entry-card-urgent .entry-card-inner { animation: urgencyPulse 2.2s ease-in-out infinite; }
        .entry-card-overdue .entry-card-inner {
          box-shadow: 0 2px 28px rgba(0,0,0,0.4), 0 0 20px 4px ${DARK_TOKENS.dangerBorderLo} !important;
          border-color: rgba(212,96,96,0.7) !important;
          border-top-color: ${DARK_TOKENS.danger} !important;
        }
        .card-grid { display: flex; flex-direction: column; gap: 12px; max-width: 680px; margin: 0 auto; width: 100%; padding-bottom: calc(140px + env(safe-area-inset-bottom)); }
        @keyframes shimmer { from { background-position: -600px 0; } to { background-position: 600px 0; } }
        .skeleton-pulse { background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.11) 50%, rgba(255,255,255,0.04) 75%); background-size: 1200px 100%; animation: shimmer 1.6s ease-in-out infinite; border-radius: 6px; }
        .glass-grain::before { content: ''; position: absolute; inset: 0; pointer-events: none; z-index: -1; border-radius: inherit;
          background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='1'/></svg>");
          background-size: 180px 180px; opacity: 0.10; }
        .entry-card { user-select: none; -webkit-user-select: none; }
        .entry-card:hover .entry-card-inner { box-shadow: ${isDark
          ? "inset 0 1.5px 0 rgba(255,255,255,0.55), inset -1px -1px 0 rgba(255,255,255,0.08), 0 16px 52px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.25)"
          : "inset 0 1.5px 0 rgba(255,255,255,0.95), inset -1px -1px 0 rgba(255,255,255,0.40), 0 12px 36px rgba(0,0,0,0.13)"}; }
        .entry-card-urgent:hover .entry-card-inner {
          animation-play-state: paused !important;
          box-shadow: 0 2px 20px rgba(0,0,0,0.4), 0 0 22px 6px rgba(251,146,60,0.45) !important;
          border-color: rgba(251,146,60,0.95) !important;
          border-top-color: rgba(251,146,60,1) !important;
        }
        .entry-card-overdue:hover .entry-card-inner {
          box-shadow: 0 2px 22px rgba(0,0,0,0.45), 0 0 22px 6px ${DARK_TOKENS.dangerBorder} !important;
          border-color: rgba(212,96,96,1) !important;
          border-top-color: #FF6B6B !important;
        }
        .ctx-menu-btn { transition: background 0.12s ease, transform 0.12s ease, padding-left 0.12s ease; }
        .ctx-menu-btn:hover { background: ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"} !important; padding-left: 20px; }
        .ctx-menu-btn:active { background: ${isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.11)"} !important; transform: scale(0.98); }
        @keyframes menuSpringIn {
          0%   { opacity: 0; transform: scale(0.72) translateY(-8px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes menuFadeOut {
          from { opacity: 1; transform: scale(1)    translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(-6px); }
        }
      `}</style>

      <div style={{ background: isDark
          ? "linear-gradient(135deg,#060912 0%,#080E1C 45%,#0A1020 70%,#060912 100%)"
          : "linear-gradient(135deg,#E8EDF6 0%,#EDF1FA 50%,#E4EAF4 100%)",
        minHeight: "100vh", color: SA.white,
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", paddingBottom: 100,
        position: "relative", overflowX: "clip" }}>
        {isDark && bgBlobs.map((b, i) => (
          <div key={i} style={{
            position: "fixed", pointerEvents: "none", zIndex: 0,
            left: `${b.left}%`, top: `${b.top}%`,
            width: b.width, height: b.height,
            transform: "translate(-50%,-50%)",
            borderRadius: "50%",
            background: `radial-gradient(ellipse at center, rgba(${b.color},${b.opacity}) 0%, transparent 70%)`,
            filter: "blur(48px)",
          }} />
        ))}


        {/* Hidden SVG specular filter */}
        <svg style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
          <defs>
            <filter id="glass-specular" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur"/>
              <feSpecularLighting in="blur" surfaceScale="4" specularConstant="1.2"
                specularExponent="25" lightingColor="white" result="spec">
                <fePointLight x="50%" y="-30%" z="120"/>
              </feSpecularLighting>
              <feComposite in="spec" in2="SourceAlpha" operator="in" result="specMasked"/>
              <feBlend in="SourceGraphic" in2="specMasked" mode="screen"/>
            </filter>
          </defs>
        </svg>

        {/* Content at z-index:1 to sit above ambient layer */}
        <div style={{ position: "relative", zIndex: 1 }}>

        {/* -- Header -- */}
        <AppHeader
          headerRef={headerRef}
          saLogoData={SA_LOGO}
          appDate={appDate}
          isWide={isWide}
          isUA={isUA}
          myAirline={myAirline}
          activeSCCO={activeSCCO}
          onMenuOpen={() => setShowMenu(true)}
          stats={stats}
          successRate={successRate}
        >


          {/* ── Sort & Filter toolbar ── */}
          <FilterBar
            sortBy={sortBy} setSortBy={setSortBy}
            sortDir={sortDir} setSortDir={setSortDir}
            sortMenuRect={sortMenuRect} setSortMenuRect={setSortMenuRect}
            isWide={isWide}
            filterInAirline={filterInAirline}   setFilterInAirline={setFilterInAirline}
            filterOutAirline={filterOutAirline}  setFilterOutAirline={setFilterOutAirline}
            filterOrigin={filterOrigin}          setFilterOrigin={setFilterOrigin}
            filterDest={filterDest}              setFilterDest={setFilterDest}
            filterStatus={filterStatus}          setFilterStatus={setFilterStatus}
            filterOverdue={filterOverdue}        setFilterOverdue={setFilterOverdue}
            filterHideP2P={filterHideP2P}
            isUA={isUA}
            sorted={sorted}
            confirmDeleteAll={confirmDeleteAll}  setConfirmDeleteAll={setConfirmDeleteAll}
            onOpenFilters={() => setShowFilterModal(true)}
            onClearFilters={() => {
              setFilterInAirline("ALL"); setFilterOutAirline("ALL");
              setFilterOrigin("ALL"); setFilterDest("ALL");
              setFilterStatus("ALL"); setFilterOverdue(false);
              setConfirmDeleteAll(false);
            }}
            onDeleteAll={async (ids) => {
              setEntries(prev => prev.filter(e => !ids.includes(e.id)));
              await Promise.all(ids.map(id => dbDeleteEntry(id)));
            }}
          />

        </AppHeader>

        {/* ── Bottom tab bar ── */}
        <div style={{
          ...GLASS.card,
          position: "fixed", bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
          left: "50%", transform: "translateX(-50%)", zIndex: 50,
          display: "flex",
          borderRadius: 100, padding: 3,
          // Light mode: flip border dark + sharper drop shadow so the pill reads against white cards
          ...(isDark ? {} : {
            background: "linear-gradient(135deg, rgba(220,228,248,0.88) 0%, rgba(210,220,244,0.72) 100%)",
            border: "1px solid rgba(0,0,0,0.10)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.80), 0 2px 8px rgba(0,0,0,0.10), 0 6px 20px rgba(0,0,0,0.10)",
          }) }}>
            {[
              ["monitoring", "Monitoring", monitoringCount],
              ["complete",   "Complete",   completeCount],
            ].map(([val, label, count]) => {
              const sel = filterCompletion === val;
              return (
                <Button variant="ghost" key={val} active={sel}
                  onClick={() => { setFilterCompletion(val); setFilterStatus("ALL"); setConfirmDeleteAll(false); }}
                  style={{
                    background: sel
                      ? (isDark ? "rgba(255,255,255,0.13)" : "rgba(255,255,255,0.95)")
                      : "transparent",
                    boxShadow: sel
                      ? (isDark ? "0 1px 4px rgba(0,0,0,0.45)" : "0 2px 8px rgba(0,0,0,0.14), 0 1px 2px rgba(0,0,0,0.08)")
                      : "none",
                    borderRadius: 100, padding: "7px 24px",
                    color: sel ? SA.white : SA.silver,
                    fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase",
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: sel ? 600 : 400,
                    transition: "background 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
                    justifyContent: "center", gap: 8, border: "none" }}>
                  {label}
                  <span style={{
                    fontSize: TYPOGRAPHY.tiny, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                    background: sel
                      ? (isDark ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.08)")
                      : "rgba(255,255,255,0.05)",
                    borderRadius: RADIUS.md, padding: "1px 6px",
                    color: sel ? SA.lightSilver : SA.silver,
                    minWidth: 18, textAlign: "center",
                    transition: "background 0.18s ease, color 0.18s ease" }}>{count}</span>
                </Button>
              );
            })}
        </div>

        {/* Glass edge scrim — soft gradient just below header simulates light refracting through the glass edge */}
        <div style={{ position: "fixed", top: `calc(${headerHeight + 8}px + env(safe-area-inset-top, 0px))`, left: 0, right: 0, height: 28, zIndex: 49, pointerEvents: "none",
          background: isDark
            ? "linear-gradient(to bottom, rgba(6,10,20,0.38) 0%, rgba(6,10,20,0.10) 60%, transparent 100%)"
            : "linear-gradient(to bottom, rgba(180,200,255,0.55) 0%, rgba(180,200,255,0.12) 60%, transparent 100%)",
        }} />

        {/* ── Scroll container — gives WebKit a proper scroll context for rubber-band overscroll ── */}
        <div style={{
          position: "fixed",
          top: 0, bottom: 0, left: 0, right: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}>

        {/* Spacer — pushes content below the fixed header; cards slide under it on scroll */}
        <div style={{ height: `calc(${headerHeight + 16}px + env(safe-area-inset-top, 0px))`, flexShrink: 0 }} />

        {/* -- Urgent message banner -- */}
        {urgentBanner && (
          <div style={{ background: tokens.dangerBgSubtle, borderBottom: `1px solid ${tokens.dangerBorder}`,
            padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, zIndex: 49 }}>
            <span style={{ fontSize: TYPOGRAPHY.tiny, color: tokens.dangerMuted, letterSpacing: "0.14em", fontWeight: 700, flexShrink: 0 }}>
              ! URGENT - {urgentBanner.from_airline}
            </span>
            <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.white, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {urgentBanner.body}
            </span>
            <Button onClick={() => { setShowMessages(true); handleDismissUrgentBanner(); }}
              style={{ background: tokens.dangerSurface, border: `1px solid ${tokens.dangerMuted}`, borderRadius: 2,
                color: tokens.dangerText, fontSize: 8, letterSpacing: "0.12em", padding: "5px 10px",
                fontFamily: "'Helvetica Neue',Arial,sans-serif",
                textTransform: "uppercase", flexShrink: 0 }}>
              View
            </Button>
            <Button variant="ghost" onClick={handleDismissUrgentBanner}
              style={{ color: SA.silver, fontSize: 16, flexShrink: 0, lineHeight: 1 }}>
              x
            </Button>
          </div>
        )}


        {/* -- Entry list -- */}
        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "16px 20px", position: "relative" }}>

          {/* Messages FAB — floating bottom-left */}
          {(() => {
            const hasUrgent = messages.some(m => {
              if (!m.urgent || m.read) return false;
              if (isUA) return m.to_airline === "UA";
              if (m.to_airline === "ALL") return !broadcastReceipts.some(r => r.message_id === m.id);
              return m.to_airline === myAirline;
            });
            const btnClass = hasUrgent ? "msg-btn-urgent" : unread > 0 ? "msg-btn-unread" : "";
            const bubbleColor = hasUrgent ? tokens.dangerMuted : unread > 0 ? tokens.warning : SA.silver;
            return (
              <Button onClick={() => setShowMessages(true)}
                className={`glass-fab ${btnClass}`}
                style={{ position: "fixed", bottom: "calc(76px + env(safe-area-inset-bottom))", left: 22, zIndex: 50,
                  width: 56, height: 56, borderRadius: RADIUS.circle,
                  backdropFilter: "blur(28px) saturate(200%)",
                  WebkitBackdropFilter: "blur(28px) saturate(200%)",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.09) 55%, rgba(255,255,255,0.04) 100%)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.64) 55%, rgba(255,255,255,0.48) 100%)",
                  border: hasUrgent
                    ? `1.5px solid ${tokens.dangerBorder}`
                    : unread > 0
                      ? "1.5px solid rgba(200,168,48,0.45)"
                      : isDark ? "1.5px solid rgba(255,255,255,0.32)" : "1.5px solid rgba(0,0,0,0.09)",
                  boxShadow: isDark
                    ? "inset 0 1.5px 0 rgba(255,255,255,0.80), inset -1px -1px 0 rgba(255,255,255,0.14), inset 0 -1px 3px rgba(0,0,0,0.12), 0 10px 25px rgba(0,0,0,0.20)"
                    : "inset 0 1.5px 0 rgba(255,255,255,0.90), 0 2px 8px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.14)",
                  justifyContent: "center",
                  transition: "transform 0.15s, filter 0.15s, box-shadow 0.15s" }}>
                <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                  {/* Bubble outline */}
                  <path d="M16 4C9.37 4 4 8.70 4 14.5c0 2.96 1.35 5.63 3.55 7.56L6 28l5.84-2.34C13.16 26.2 14.55 26.5 16 26.5c6.63 0 12-4.70 12-10.5S22.63 4 16 4z"
                    stroke={isDark ? "rgba(255,255,255,0.90)" : "rgba(10,20,60,0.75)"}
                    strokeWidth="2" strokeLinejoin="round" fill="none"/>
                  {/* Three dots */}
                  <circle cx="10.5" cy="14.5" r="1.6"
                    fill={hasUrgent ? "#ff6b6b" : unread > 0 ? "#f5c842" : (isDark ? "rgba(255,255,255,0.80)" : "rgba(10,20,60,0.65)")}/>
                  <circle cx="16" cy="14.5" r="1.6"
                    fill={hasUrgent ? "#ff6b6b" : unread > 0 ? "#f5c842" : (isDark ? "rgba(255,255,255,0.80)" : "rgba(10,20,60,0.65)")}/>
                  <circle cx="21.5" cy="14.5" r="1.6"
                    fill={hasUrgent ? "#ff6b6b" : unread > 0 ? "#f5c842" : (isDark ? "rgba(255,255,255,0.80)" : "rgba(10,20,60,0.65)")}/>
                </svg>
                {unread > 0 && (
                  <span style={{ position: "absolute", top: 0, right: 0,
                    background: hasUrgent ? tokens.dangerMuted : tokens.warning,
                    color: "#fff",
                    fontSize: 8, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                    borderRadius: RADIUS.md, padding: "1px 5px", minWidth: 16,
                    textAlign: "center", fontWeight: 700, lineHeight: "16px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
                    {unread}
                  </span>
                )}
              </Button>
            );
          })()}

        <div className="card-grid">
          {isUA && hiddenIds.size > 0 && (
            <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
              <button onClick={handleUnhideAll} style={{ background: "none", border: "none", cursor: "pointer",
                fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5b8eff",
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                Unhide Cards ({hiddenIds.size})
              </button>
            </div>
          )}
          {sorted.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0" }}>
              <img src={SA_LOGO} alt="" style={{ width: 80, height: 80, objectFit: "contain",
                mixBlendMode: isDark ? "screen" : "normal",
                filter: isDark ? "none" : "invert(1)",
                opacity: isDark ? 0.35 : 0.18 }} />
              <div style={{ color: SA.borderGray, fontSize: TYPOGRAPHY.tiny, letterSpacing: "0.2em", marginTop: 16, textTransform: "uppercase" }}>
                {!isUA
                ? "No connections today"
                : filterCompletion === "monitoring"
                  ? "No connections being monitored"
                  : `No completed connections${filterStatus !== "ALL" ? ` -- ${filterStatus}` : ""}`}
              </div>
            </div>
          ) : (
            loading
              ? [0,1,2].map(i => (
                  <div key={i} style={{ ...GLASS.card, borderLeft: `3px solid ${SA.borderGray}`, borderRadius: RADIUS.card, padding: 0, overflow: "hidden", opacity: 1 - i * 0.2 }}>
                    <div style={{ padding: "18px 20px 14px" }}>
                      {/* Flight row */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                        <div className="skeleton-pulse" style={{ width: 38, height: 38, borderRadius: 8, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div className="skeleton-pulse" style={{ height: 18, width: 80, marginBottom: 6 }} />
                          <div className="skeleton-pulse" style={{ height: 11, width: 120 }} />
                        </div>
                        <div className="skeleton-pulse" style={{ width: 28, height: 1, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div className="skeleton-pulse" style={{ height: 18, width: 80, marginBottom: 6 }} />
                          <div className="skeleton-pulse" style={{ height: 11, width: 100 }} />
                        </div>
                        <div className="skeleton-pulse" style={{ width: 38, height: 38, borderRadius: 8, flexShrink: 0 }} />
                      </div>
                      {/* Status row */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="skeleton-pulse" style={{ height: 22, width: 90, borderRadius: 20 }} />
                        <div className="skeleton-pulse" style={{ height: 22, width: 64, borderRadius: 20 }} />
                      </div>
                    </div>
                  </div>
                ))
              : displayedEntries.map((e, idx) => {
                  const msgCount = messages.filter(m => m.connection_id === e.id).length;
                  return (
                    <div key={e.id} style={{ animation: "cardEntrance 0.4s ease-out both", animationDelay: `${Math.min(idx, 12) * 50}ms` }}>
                      <EntryCard entry={{...e, _msgCount: msgCount}} onDelete={isUA ? handleDelete : undefined} onEdit={isUA ? (entry) => { clearNewEntryId(entry.id); setEditEntry(entry); } : undefined} onReopen={isUA ? handleReopen : undefined} onMarkSuccessful={isUA ? handleMarkSuccessful : undefined} onView={(entry) => { clearNewEntryId(entry.id); setViewEntry(entry); }} isUA={isUA} myAirline={myAirline} onRemark={(entry) => isUA ? setUaRemark(entry) : setPartnerRemark(entry)} onBoarded={!isUA ? (entry) => setPartnerBoarded(entry) : undefined} onWillHold={(entry) => setWillHoldEntry(entry)} onPin={isUA && filterCompletion === "monitoring" ? handlePin : undefined} isNew={newEntryIds.has(e.id)} onMarkSeen={() => clearNewEntryId(e.id)} isImported={importedEntryIds.has(e.id)} appDate={appDate} onAmenityRequest={!isUA ? (entry) => setAmenityEntry(entry) : undefined} amenityStatus={amenityStatusMap[e.id]?.status || null} onHide={isUA ? (entry) => handleHideEntry(entry.id) : undefined} onMessage={(entry) => {
                          setMessageDraft(entry);
                          if (!isUA) {
                            setOpenThread("UA");
                          } else {
                            const inAl  = getAirlineCode(entry.inAirline, entry.inFlt);
                            const outAl = getAirlineCode(entry.outAirline, entry.outFlt);
                            const partner = inAl !== "UA" ? inAl : outAl !== "UA" ? outAl : null;
                            if (partner) setOpenThread(LHG_CARRIERS.includes(partner) ? "LHG" : partner);
                          }
                          setShowMessages(true);
                        }}
                      />
                    </div>
                  );
                })
          )}
        </div>
        </div>
        </div>{/* end scroll container */}

        {/* -- FAB -- UA only -- */}
        {isUA && (
          <Button onClick={() => setShowEntry(true)}
            className="glass-fab"
            style={{ position: "fixed", bottom: "calc(76px + env(safe-area-inset-bottom))", right: 22, width: 56, height: 56, borderRadius: RADIUS.circle,
              backdropFilter: "blur(28px) saturate(200%)", WebkitBackdropFilter: "blur(28px) saturate(200%)",
              background: isDark
                ? "linear-gradient(135deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.12) 55%, rgba(255,255,255,0.06) 100%)"
                : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.74) 55%, rgba(255,255,255,0.56) 100%)",
              border: isDark ? "1.5px solid rgba(255,255,255,0.38)" : "1.5px solid rgba(0,0,0,0.09)",
              boxShadow: isDark
                ? "inset 0 1.5px 0 rgba(255,255,255,0.90), inset -1px -1px 0 rgba(255,255,255,0.18), inset 0 -1px 3px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.16)"
                : "inset 0 1.5px 0 rgba(255,255,255,0.90), 0 2px 6px rgba(0,0,0,0.10), 0 6px 18px rgba(0,0,0,0.11)",
              justifyContent: "center",
              transition: "transform 0.15s, filter 0.15s, box-shadow 0.15s" }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <line x1="11" y1="3" x2="11" y2="19" stroke={isDark ? "rgba(255,255,255,0.92)" : "rgba(10,20,60,0.78)"} strokeWidth="2.2" strokeLinecap="round"/>
              <line x1="3" y1="11" x2="19" y2="11" stroke={isDark ? "rgba(255,255,255,0.92)" : "rgba(10,20,60,0.78)"} strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
          </Button>
        )}
      </div>{/* end zIndex:1 content wrapper */}
      </div>{/* end ambient/outer wrapper */}

      {/* -- Filter Modal -- */}
      {showFilterModal && (() => {
        const opts = filterOptions;
        const sectionLabel = (text) => (
          <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.14em",
            textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
            marginBottom: 10, marginTop: 4 }}>{text}</div>
        );
        const selectStyle = {
          width: "100%", boxSizing: "border-box",
          background: SA.darkGray, border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm,
          color: SA.white, padding: "9px 32px 9px 12px", fontSize: TYPOGRAPHY.body, cursor: "pointer",
          fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none",
          appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
          colorScheme: "dark",
        };
        const activeVal = (val) => val !== "ALL" && val !== false;
        return (
          <div style={{ ...overlayBackdrop,
            zIndex: 10000, display: "flex", alignItems: "flex-end", justifyContent: "center",
            padding: "0", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}
            onClick={() => setShowFilterModal(false)}>
            <div onClick={e => e.stopPropagation()}
              style={{ ...GLASS.modal, width: "100%", maxWidth: 480,
                borderTop: `2px solid ${isDark ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.9)"}`,
                borderRadius: "24px 24px 0 0", padding: "0 0 32px",
                animation: "filterSheetUp 0.24s cubic-bezier(0.16,1,0.3,1)",
                ...filterDragStyle }}>

              {/* Drag handle */}
              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                touchAction: "none", cursor: "grab" }} {...filterHandleProps}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
              </div>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 20px 14px" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: SA.white,
                    letterSpacing: "-0.02em" }}>Filter Connections</div>
                  <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 3 }}>
                    {sorted.length} result{sorted.length !== 1 ? "s" : ""} shown
                  </div>
                </div>
                <Button variant="icon" onClick={() => setShowFilterModal(false)}
                  style={{ background: "rgba(255,255,255,0.07)", color: SA.silver, width: 30, height: 30, fontSize: 16 }}>
                  ×
                </Button>
              </div>
              <div style={{ height: 1, background: SA.borderGray, margin: "0 0 16px" }} />

              <div style={{ padding: "0 20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 14px" }}>

                {/* Inbound airline */}
                <div>
                  {sectionLabel("Inbound Airline")}
                  <select value={filterInAirline} onChange={e => setFilterInAirline(e.target.value)}
                    style={{ ...selectStyle, borderColor: activeVal(filterInAirline) ? SA.silver : SA.borderGray }}>
                    <option value="ALL" style={{ background: SA.darkGray, color: SA.white }}>All</option>
                    {opts.inAirlines.map(al => <option key={al} value={al} style={{ background: SA.darkGray, color: SA.white }}>{al}</option>)}
                  </select>
                </div>

                {/* Outbound airline */}
                <div>
                  {sectionLabel("Outbound Airline")}
                  <select value={filterOutAirline} onChange={e => setFilterOutAirline(e.target.value)}
                    style={{ ...selectStyle, borderColor: activeVal(filterOutAirline) ? SA.silver : SA.borderGray }}>
                    <option value="ALL" style={{ background: SA.darkGray, color: SA.white }}>All</option>
                    {opts.outAirlines.map(al => <option key={al} value={al} style={{ background: SA.darkGray, color: SA.white }}>{al}</option>)}
                  </select>
                </div>

                {/* Origin */}
                <div>
                  {sectionLabel("Origin Airport")}
                  <select value={filterOrigin} onChange={e => setFilterOrigin(e.target.value)}
                    style={{ ...selectStyle, borderColor: activeVal(filterOrigin) ? SA.silver : SA.borderGray }}>
                    <option value="ALL" style={{ background: SA.darkGray, color: SA.white }}>All</option>
                    {opts.origins.map(o => <option key={o} value={o} style={{ background: SA.darkGray, color: SA.white }}>{o}</option>)}
                  </select>
                </div>

                {/* Destination */}
                <div>
                  {sectionLabel("Destination")}
                  <select value={filterDest} onChange={e => setFilterDest(e.target.value)}
                    style={{ ...selectStyle, borderColor: activeVal(filterDest) ? SA.silver : SA.borderGray }}>
                    <option value="ALL" style={{ background: SA.darkGray, color: SA.white }}>All</option>
                    {opts.dests.map(d => <option key={d} value={d} style={{ background: SA.darkGray, color: SA.white }}>{d}</option>)}
                  </select>
                </div>

                {/* Status — full width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  {sectionLabel("Status")}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["ALL", ...STATUS_OPTIONS].map(s => {
                      const sel = filterStatus === s;
                      const st  = s !== "ALL" ? STATUS_STYLES[s] : null;
                      return (
                        <Button key={s} onClick={() => setFilterStatus(s)}
                          style={{ padding: "6px 14px", borderRadius: RADIUS.pill, fontSize: TYPOGRAPHY.caption,
                            fontWeight: sel ? 600 : 400, whiteSpace: "nowrap",
                            fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.04em",
                            transition: "all 0.15s",
                            background: sel ? (st ? st.bg  : SA.medGray)    : "transparent",
                            border:     `1px solid ${sel ? (st ? st.border : SA.silver) : SA.borderGray}`,
                            color:      sel ? (st ? st.text : SA.white)     : SA.silver }}>
                          {s === "ALL" ? "Any status" : s}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Departure time range — full width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  {sectionLabel("Departure Time (STD/ETD)")}
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="text" inputMode="numeric" placeholder="HH:MM"
                      value={filterTimeFrom}
                      onChange={e => {
                        let raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                        setFilterTimeFrom(raw.length >= 3 ? raw.slice(0,2) + ":" + raw.slice(2) : raw);
                      }}
                      style={{ ...selectStyle, flex: 1, backgroundImage: "none",
                        borderColor: filterTimeFrom ? SA.silver : SA.borderGray }} />
                    <span style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, flexShrink: 0 }}>to</span>
                    <input type="text" inputMode="numeric" placeholder="HH:MM"
                      value={filterTimeTo}
                      onChange={e => {
                        let raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                        setFilterTimeTo(raw.length >= 3 ? raw.slice(0,2) + ":" + raw.slice(2) : raw);
                      }}
                      style={{ ...selectStyle, flex: 1, backgroundImage: "none",
                        borderColor: filterTimeTo ? SA.silver : SA.borderGray }} />
                    {(filterTimeFrom || filterTimeTo) && (
                      <Button variant="icon"
                        onClick={() => { setFilterTimeFrom(""); setFilterTimeTo(""); }}
                        style={{ flexShrink: 0, background: "rgba(255,255,255,0.07)",
                          color: SA.silver, width: 30, height: 30, fontSize: 16 }}
                        title="Clear time filter">×</Button>
                    )}
                  </div>
                </div>

                {/* Flags — full width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  {sectionLabel("Flags")}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Button onClick={() => setFilterOverdue(v => !v)}
                      style={{ gap: 10, padding: "10px 14px",
                        width: "100%", background: filterOverdue ? tokens.dangerBgGlow : "transparent",
                        border: `1px solid ${filterOverdue ? tokens.dangerBorder : SA.borderGray}`,
                        borderRadius: RADIUS.md, textAlign: "left" }}>
                      <div style={{ width: 18, height: 18, borderRadius: RADIUS.sharp, flexShrink: 0,
                        background: filterOverdue ? tokens.danger : "transparent",
                        border: `1.5px solid ${filterOverdue ? tokens.danger : SA.borderGray}`,
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {filterOverdue && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: TYPOGRAPHY.body, color: filterOverdue ? tokens.danger : SA.white,
                          fontWeight: 500 }}>Overdue only</div>
                        <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 2 }}>
                          Past departure time with no status set
                        </div>
                      </div>
                    </Button>
                    <Button onClick={() => setFilterHideP2P(v => !v)}
                      style={{ gap: 10, padding: "10px 14px",
                        width: "100%", background: filterHideP2P ? "rgba(74,124,247,0.10)" : "transparent",
                        border: `1px solid ${filterHideP2P ? SA.accent : SA.borderGray}`,
                        borderRadius: RADIUS.md, textAlign: "left" }}>
                      <div style={{ width: 18, height: 18, borderRadius: RADIUS.sharp, flexShrink: 0,
                        background: filterHideP2P ? SA.accent : "transparent",
                        border: `1.5px solid ${filterHideP2P ? SA.accent : SA.borderGray}`,
                        display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {filterHideP2P && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: TYPOGRAPHY.body, color: filterHideP2P ? SA.accent : SA.white,
                          fontWeight: 500 }}>Hide partner-to-partner</div>
                        <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 2 }}>
                          Show only connections involving United
                        </div>
                      </div>
                    </Button>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div style={{ padding: "20px 20px 0", display: "flex", gap: 10 }}>
                <Button variant="secondary" onClick={() => {
                  setFilterInAirline("ALL"); setFilterOutAirline("ALL");
                  setFilterOrigin("ALL"); setFilterDest("ALL");
                  setFilterStatus("ALL"); setFilterOverdue(false); setFilterHideP2P(false);
                  setFilterTimeFrom(""); setFilterTimeTo("");
                }}
                  style={{ flex: 1, padding: "12px",
                    borderRadius: 12,
                    color: SA.silver, fontSize: TYPOGRAPHY.note, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  Clear all
                </Button>
                <Button variant="primary" onClick={() => setShowFilterModal(false)}
                  style={{ flex: 2, padding: "12px",
                    borderRadius: 12,
                    color: "#fff", fontSize: TYPOGRAPHY.note, fontWeight: 600,
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  Show {sorted.length} result{sorted.length !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {viewEntry && <ViewEntryModal entry={viewEntry} onClose={() => setViewEntry(null)} />}
      {showEntry   && isUA && <AddEntryModal SA={SA} GLASS={GLASS} STATUS_STYLES={STATUS_STYLES} onClose={() => setShowEntry(false)} onSave={handleSave} shift={shift} activeSCCO={activeSCCO} airline={myAirline} />}
      {editEntry   && isUA && <AddEntryModal SA={SA} GLASS={GLASS} STATUS_STYLES={STATUS_STYLES} onClose={() => setEditEntry(null)}  onSave={handleEdit} shift={shift} initialEntry={editEntry} onDelete={handleDelete} onReopen={handleReopen} activeSCCO={activeSCCO} airline={myAirline} />}
      {showMenu   && <HamburgerMenu appDate={appDate} entries={visibleEntries} shift={shift}
        onImportExcel={() => { setShowMenu(false); setShowImportExcel(true); }}
        isUA={isUA}
        onChangeDate={() => setShowChangeDate(true)}
        activeSCCO={activeSCCO}
        onEditShift={() => setShowShiftSetup(true)}

        isAdmin={isAdmin}
        onManagePasswords={() => setShowAdminPw(true)}
        onManageRoster={() => setShowSCCORoster(true)}
        onChangeMyPassword={() => setShowChangePw(true)}
        onExportShift={() => setShowExportShift(true)}
        onPhoneDirectory={() => setShowPhoneDir(true)}
        onFeedback={() => setShowFeedback(true)}
        onFeedbackInbox={() => setShowFeedbackInbox(true)}
        unreadFeedback={unreadFeedback}
        onLoginAlerts={() => setShowLoginAlerts(true)}
        lockoutAlertCount={lockoutAlertCount}
        onLoginLog={() => setShowLoginLog(true)}
        onActivityHeatmap={() => setShowActivityHeatmap(true)}
        onOpsIntel={() => setShowOpsIntel(true)}
        onWarRoom={async () => {
          const { data } = await supabaseClient.auth.getSession();
          if (data?.session) {
            localStorage.setItem("sa_mc_bridge", JSON.stringify({
              access_token:  data.session.access_token,
              refresh_token: data.session.refresh_token,
              app_session:   session,
              active_scco:   activeSCCO,
              ts: Date.now(),
            }));
          }
          mcPopupRef.current = window.open(
            `${window.location.origin}${window.location.pathname}?mc=1`,
            "MissionControl",
            "popup,width=1440,height=900,menubar=no,toolbar=no,location=no,status=no"
          );
        }}
        syncEnabled={syncEnabled}
        onToggleSync={handleToggleSync}
        onSignOut={() => { explicitSignOutRef.current = true; doSignOut(); }}
        pushEnabled={pushEnabled}
        onTogglePush={async () => {
          if (pushEnabled) {
            await unsubscribeFromPush(isUA ? "UA" : myAirline);
            setPushEnabled(false);
            savePushPref(false);
          } else {
            const perm = await requestNotificationPermission();
            setPushPermission(perm);
            if (perm === 'granted') {
              const sub = await subscribeToPush(isUA ? "UA" : myAirline);
              if (sub) {
                setPushEnabled(true);
                savePushPref(true);
                sendPushNotification('Star Alliance LAX', 'Push notifications are enabled ✓', 'sa-enable');
              } else {
                alert("Push notifications could not be enabled.\n\nTry refreshing the app and trying again. If the problem persists, make sure the app is installed as a PWA and notifications are allowed in your browser settings.");
              }
            } else {
              setPushEnabled(false);
              savePushPref(false);
              alert("Notifications are blocked.\n\nTo enable push notifications, click the lock icon in your browser's address bar, set Notifications to 'Allow', then try again.");
            }
          }
        }}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        fontScale={fontScale}
        onSetFontScale={setFontScale}
        onExpeditorMode={() => setShowExpeditor(true)}
        onClose={() => setShowMenu(false)} />}
      {showChangeDate && <ChangeDateModal currentDate={appDate} onConfirm={handleChangeDate} onClose={() => setShowChangeDate(false)} />}
      {globalDragging && isUA && session && !showImportExcel && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none",
          background: "rgba(0,8,24,0.85)", backdropFilter: "blur(6px)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          border: "3px dashed rgba(74,124,247,0.55)" }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>📂</div>
          <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.white, letterSpacing: "0.22em",
            textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
            fontWeight: 700, marginBottom: 6 }}>Drop to Import Excel</div>
          <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
            letterSpacing: "0.1em" }}>.xlsx · .xls · .csv</div>
        </div>
      )}
      {showImportExcel && isUA && (
        <ExcelImportModal
          parsedShift={parsedShift}
          existingEntries={entries}
          onImport={handleImport}
          initialFile={initialImportFile}
          onClose={() => { setShowImportExcel(false); setInitialImportFile(null); }}
        />
      )}
      {showShiftSetup && (
        <ShiftSetupModal
          initialShift={shift || ""}
          onSave={handleShiftSave}
          onClose={() => setShowShiftSetup(false)}
          isFirstTime={false}
          onImportExcel={() => { setShowShiftSetup(false); setShowImportExcel(true); }}
        />
      )}
      {showExportShift && (
        <ExportShiftModal
          SA={SA}
          GLASS={GLASS}
          entries={visibleEntries}
          dateLabel={formatDateLabel(appDate)}
          saLogoData={SA_LOGO}
          activeSCCO={activeSCCO}
          isAdmin={isAdmin}
          onClose={() => setShowExportShift(false)}
        />
      )}
      {showAdminPw    && isAdmin && <AdminPasswordModal onClose={() => setShowAdminPw(false)} />}
      {showChangePw   && activeSCCO && isUA && (
        <ChangePasswordModal sccoId={activeSCCO.id} sccoName={activeSCCO.name} onClose={() => setShowChangePw(false)} />
      )}
      {showInactivity && isUA && (
        <InactivityModal
          secondsLeft={inactivitySecsLeft}
          onStillHere={resetInactivity}
        />
      )}
      {showSCCORoster && isAdmin && <SCCORosterModal onClose={() => setShowSCCORoster(false)} />}
      {showPhoneDir && (
        <PhoneDirectoryModal
          isUA={isUA}
          myAirline={myAirline}
          isAdmin={isAdmin}
          onClose={() => setShowPhoneDir(false)}
        />
      )}
      {showFeedback && (
        <FeedbackModal
          reporter={isUA ? (activeSCCO?.name || "SCCO") : (partnerName || myAirline)}
          airline={isUA ? "UA" : myAirline}
          currentPage={activeTab}
          onClose={() => setShowFeedback(false)}
        />
      )}
      {showFeedbackInbox && isAdmin && (
        <FeedbackInboxModal onClose={() => setShowFeedbackInbox(false)} />
      )}
      {showLoginAlerts && isAdmin && (
        <LoginAlertsModal onClose={() => setShowLoginAlerts(false)} />
      )}
      {showLoginLog && isAdmin && (
        <LoginLogModal onClose={() => setShowLoginLog(false)} />
      )}
      {showActivityHeatmap && isAdmin && (
        <ActivityHeatmapModal messages={messages} onClose={() => setShowActivityHeatmap(false)} />
      )}
      {showOpsIntel && isAdmin && (
        <OpsIntelModal entries={entries} appDate={appDate} onClose={() => setShowOpsIntel(false)} />
      )}
      {showWarRoom && (
        <WarRoomModal
          entries={warRoomEntries}
          isDark={isDark}
          onClose={() => window.close()}
        />
      )}
      {showExpeditor && (
        <ExpeditorModal
          entries={visibleEntries}
          saLogoData={SA_LOGO}
          onClose={() => setShowExpeditor(false)}
        />
      )}
      {showMessages && (
        <MessagesModal
          GLASS={GLASS}
          messages={messages}
          isUA={isUA}
          isAdmin={isAdmin}
          myAirline={myAirline}
          appDate={appDate}
          entries={visibleEntries}
          initialLinkEntry={messageDraft}
          onClose={() => { setShowMessages(false); setOpenThread(null); setMessageDraft(null); loadMessages(); }}
          onSend={handleSendMessage}
          onBroadcast={handleBroadcast}
          onDelete={handleDeleteMessage}
          onDeleteAll={handleDeleteAllMessages}
          onMarkRead={handleMarkRead}
          broadcastReceipts={broadcastReceipts}
          onRecordBroadcastReceipts={handleRecordBroadcastReceipts}
          onLoadBroadcastReceipts={handleLoadBroadcastReceipts}
          onOpenEntry={(entry) => {
            setShowMessages(false);
            setOpenThread(null);
            if (isUA) setEditEntry(entry);
            else setViewEntry(entry);
          }}
          openThread={openThread}
          isDark={isDark}
          onAmenityResponse={handleAmenityResponseWithRemark}
        />
      )}
      {amenityEntry && (
        <AmenityRequestModal
          entry={amenityEntry}
          onClose={() => setAmenityEntry(null)}
          onSend={async ({ amenityType, note }) => {
            await handleSendMessage({
              date: appDate,
              from_airline: myAirline,
              to_airline: "UA",
              body: note || "",
              urgent: false,
              read: false,
              connection_id: amenityEntry.id,
              msg_type: "amenity_request",
              amenity_types: amenityType,
              amenity_status: "pending",
            });
            const typeLabel = amenityType === "both" ? "hotel and meals" : amenityType;
            const withRemark = { ...amenityEntry, remark: appendRemark(amenityEntry.remark, `Amenity request submitted: ${typeLabel}`, myAirline, partnerName || myAirline) };
            await dbUpdateEntry(withRemark, appDate);
            setEntries(prev => prev.map(e => e.id === withRemark.id ? withRemark : e));
            setAmenityEntry(null);
          }}
        />
      )}
      {partnerRemark && (
        <PartnerRemarkModal
          entry={partnerRemark}
          airline={myAirline}
          partnerName={partnerName}
          date={appDate}
          onClose={() => setPartnerRemark(null)}
          onSave={(updated) => {
            setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
            setPartnerRemark(null);
          }}
        />
      )}
      {uaRemark && (
        <PartnerRemarkModal
          entry={uaRemark}
          airline={myAirline}
          partnerName={activeSCCO?.name}
          date={appDate}
          onClose={() => setUaRemark(null)}
          onSave={(updated) => {
            setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
            setUaRemark(null);
          }}
        />
      )}
      {partnerBoarded && (
        <PartnerBoardedModal
          entry={partnerBoarded}
          airline={myAirline}
          partnerName={partnerName}
          date={appDate}
          appDate={appDate}
          onClose={() => setPartnerBoarded(null)}
          onSave={(updated) => {
            setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
            setPartnerBoarded(null);
          }}
          onSendMessage={async (msg) => {
            await dbSendMessage({
              ...msg,
              from_scco_name: partnerName || null,
            });
            loadMessages();
          }}
        />
      )}
      {willHoldEntry && (
        <WillHoldModal
          entry={willHoldEntry}
          date={appDate}
          appDate={appDate}
          airline={myAirline || "UA"}
          partnerName={partnerName}
          onClose={() => setWillHoldEntry(null)}
          onSave={(updated) => {
            setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
            setWillHoldEntry(null);
          }}
          onSendMessage={async (msg) => {
            await dbSendMessage({ ...msg, from_scco_name: partnerName || null });
            loadMessages();
          }}
        />
      )}
      {showSCCOPicker && isUA && (
        <SCCOPicker
          sccoList={sccoList}
          activeSCCO={activeSCCO}
          isAdmin={isAdmin}
          onSelect={(scco) => { setActiveSCCO(scco); setShowSCCOPicker(false); }}
          onAddNew={handleSCCOAddNew}
          onClose={() => setShowSCCOPicker(false)}
        />
      )}
      <IOSInstallBanner saLogoData={SA_LOGO} />
    </>
    </ThemeContext.Provider>
  );
}
